<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const props = withDefaults(defineProps<{
  images: string[];
  isOpen: boolean;
  initialIndex?: number;
  copyableTextHtml?: string;
}>(), {
  initialIndex: 0,
  copyableTextHtml: '',
});

const emit = defineEmits<{
  close: [];
}>();

const currentIndex = ref(props.initialIndex);
const showCopyableText = ref(false);
const closeButtonRef = ref<HTMLButtonElement | null>(null);
let previouslyFocusedElement: HTMLElement | null = null;
let previousBodyOverflow = '';
let previousRootOverflow = '';

const currentImage = computed(() => props.images[currentIndex.value] || '');
const hasMultipleImages = computed(() => props.images.length > 1);
const hasCopyableText = computed(() => {
  const plainText = props.copyableTextHtml
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return plainText.length > 0;
});

watch(() => props.isOpen, async (isOpen, wasOpen) => {
  if (isOpen) {
    currentIndex.value = Math.min(props.initialIndex, Math.max(0, props.images.length - 1));
    showCopyableText.value = false;
    previouslyFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    previousBodyOverflow = document.body.style.overflow;
    previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    await nextTick();
    closeButtonRef.value?.focus();
  } else if (wasOpen) {
    document.body.style.overflow = previousBodyOverflow;
    document.documentElement.style.overflow = previousRootOverflow;
    showCopyableText.value = false;
    previouslyFocusedElement?.focus();
    previouslyFocusedElement = null;
  }
});

function nextImage() {
  currentIndex.value = currentIndex.value < props.images.length - 1
    ? currentIndex.value + 1
    : 0;
}

function prevImage() {
  currentIndex.value = currentIndex.value > 0
    ? currentIndex.value - 1
    : props.images.length - 1;
}

function close() {
  emit('close');
}

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    close();
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.isOpen) return;

  switch (event.key) {
    case 'Escape':
      close();
      break;
    case 'ArrowLeft':
      if (hasMultipleImages.value) prevImage();
      break;
    case 'ArrowRight':
      if (hasMultipleImages.value) nextImage();
      break;
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  if (props.isOpen) {
    document.body.style.overflow = previousBodyOverflow;
    document.documentElement.style.overflow = previousRootOverflow;
    previouslyFocusedElement?.focus();
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="lightbox">
      <div
        v-if="isOpen"
        class="modal-overlay lightbox-backdrop"
        @click="handleBackdropClick"
      >
        <section
          class="modal-content lightbox-container"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged classified display listing"
        >
          <button
            ref="closeButtonRef"
            type="button"
            class="lightbox-close"
            aria-label="Close enlarged listing"
            @click="close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div class="lightbox-stage">
            <button
              v-if="hasMultipleImages"
              type="button"
              class="lightbox-nav lightbox-prev"
              aria-label="Previous image"
              @click="prevImage"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            <img :src="currentImage" alt="Enlarged listing" class="lightbox-image" />

            <button
              v-if="hasMultipleImages"
              type="button"
              class="lightbox-nav lightbox-next"
              aria-label="Next image"
              @click="nextImage"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>

            <div v-if="hasMultipleImages" class="lightbox-counter">
              {{ currentIndex + 1 }} / {{ images.length }}
            </div>
          </div>

          <div v-if="hasCopyableText" class="lightbox-tools">
            <button
              type="button"
              class="copyable-text-toggle"
              :aria-expanded="showCopyableText"
              @click="showCopyableText = !showCopyableText"
            >
              {{ showCopyableText ? 'Hide Text' : 'Show Text (for copying)' }}
            </button>
          </div>

          <div v-if="hasCopyableText && showCopyableText" class="copyable-text-panel">
            <div class="copyable-text-content" v-html="copyableTextHtml"></div>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.9);
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(1100px, 85vw);
  height: min(800px, 85vh);
  min-width: min(320px, 90vw);
  max-width: 85vw;
  max-height: 85vh;
  overflow: hidden;
  background: #111;
  border-radius: var(--radius-md);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.lightbox-stage {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  background: #111;
}

.lightbox-image {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  image-orientation: from-image;
}

.lightbox-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: #fff;
  background: rgba(20, 20, 20, 0.82);
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.45);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.lightbox-close:hover,
.lightbox-close:focus-visible {
  background: #555;
  transform: scale(1.06);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  border: 0;
  border-radius: var(--radius-full);
  cursor: pointer;
  transform: translateY(-50%);
  transition: background 0.2s;
}

.lightbox-nav:hover,
.lightbox-nav:focus-visible {
  background: rgba(0, 0, 0, 0.78);
}

.lightbox-prev {
  left: 12px;
}

.lightbox-next {
  right: 12px;
}

.lightbox-counter {
  position: absolute;
  bottom: 12px;
  left: 50%;
  padding: 6px 12px;
  color: #fff;
  font-size: 0.8125rem;
  background: rgba(0, 0, 0, 0.68);
  border-radius: var(--radius-full);
  transform: translateX(-50%);
}

.lightbox-tools {
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  padding: 10px 16px;
  background: #191919;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.copyable-text-toggle {
  padding: 8px 14px;
  color: var(--button-text-color);
  font-size: 0.875rem;
  font-weight: 500;
  background: var(--button-background);
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
}

.copyable-text-toggle:hover,
.copyable-text-toggle:focus-visible {
  filter: brightness(0.94);
}

.copyable-text-panel {
  box-sizing: border-box;
  flex: 0 0 auto;
  min-height: 0;
  max-height: 50%;
  padding: 20px 24px;
  overflow: auto;
  color: var(--text-primary);
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
}

.copyable-text-content {
  font-size: 1.125rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.copyable-text-content :deep(*) {
  font-size: inherit !important;
  line-height: inherit !important;
}

.copyable-text-content :deep(p) {
  margin: 0 0 1em;
}

.copyable-text-content :deep(p:last-child) {
  margin-bottom: 0;
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 8px;
  }

  .modal-content {
    width: calc(100vw - 16px);
    height: calc(100vh - 16px);
    min-width: 0;
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
  }

  .lightbox-nav {
    width: 44px;
    height: 44px;
  }

  .lightbox-image {
    width: 100%;
    height: 100%;
  }

  .lightbox-prev {
    left: 8px;
  }

  .lightbox-next {
    right: 8px;
  }

  .copyable-text-panel {
    padding: 16px;
  }
}
</style>
